import { createSlice } from "@reduxjs/toolkit";
import { defaultRequestBody, DefaultRequestBodyType } from "../../../constant/document";
import { closeCreateOrganizationsModal, closeDeleteOrganizationsModal, closeTemplateModal, createOrganizations, deleteOrganizations, fetchOrganizations, fetchPaginateOrganizations, fetchRefreshOrganizations, openCreateOrganizationsModal, openDeleteOrganizationsModal, openTemplateModal, resetOrganizationsTable, setCurrentRecord, setOrganizationsAutoRefresh, stopOrganizationsAutoRefresh, switchOffOrganization, switchOnOrganization, switchOrganizationsAutoFresh, updateOrganizationsRequestBody } from "./OrganizationsPageAction";

export interface PaginationType {
  page: number;
  pageSize: number;
  hasMore: boolean;
  loading: boolean;
}
interface AutoRefreshStateType {
  autoRefresh: boolean;
  currentIntervalId: NodeJS.Timer | undefined;
  error: string | undefined;
  loading: boolean;
}

interface OrganizationPageStateType {
  data: any;
  loading: boolean;
  error: string | undefined;
  currentRequestBody: DefaultRequestBodyType;
  pagination: PaginationType;
  autoRefreshOrganizationState: AutoRefreshStateType;
  success: boolean;
  message: string;
  createOrganizationLoading: boolean;
  deleteOrganizationLoading: boolean;
  switchLoading: boolean;
  currentRecord: any;

  //Modal
  createOrganizationsModalState: boolean;

  deleteOrganizationsModalState: boolean;
  templateModalState: boolean;
}

const OrganizationsInitialState: OrganizationPageStateType = {
  data: [],
  loading: false,
  error: undefined,
  currentRequestBody: defaultRequestBody,
  pagination: {
    page: 2,
    pageSize: 30,
    hasMore: true,
    loading: false,
  },
  autoRefreshOrganizationState: {
    autoRefresh: false,
    currentIntervalId: undefined,
    error: "",
    loading: false,
  },
  success: false,
  createOrganizationLoading: false,
  deleteOrganizationLoading: false,
  switchLoading: false,
  currentRecord: {},
  message: "",

  //Modal
  createOrganizationsModalState: false,
  deleteOrganizationsModalState: false,
  templateModalState: false,
};

const OrganizationsPageSlice = createSlice({
  initialState: OrganizationsInitialState,
  name: "transcribe",
  reducers: {},
  extraReducers(builder) {
    builder
      //Fetch Organziations data from API
      .addCase(fetchOrganizations.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      //Infinite Scrolling
      .addCase(fetchPaginateOrganizations.pending, (state, action) => {
        state.pagination.loading = true;
      })
      .addCase(fetchPaginateOrganizations.fulfilled, (state, action) => {
        state.pagination.page = state.pagination.page + 1;
        state.data = state.data.concat(action.payload);
        state.pagination.hasMore = action.payload.length === state.pagination.pageSize;
        state.pagination.loading = false;
      })
      .addCase(fetchPaginateOrganizations.rejected, (state, action) => {
        state.error = action.error.message;
        state.pagination.loading = false;
      })
      //Search Organizations
      .addCase(updateOrganizationsRequestBody, (state, action) => {
        state.currentRequestBody = action.payload;
      })
      .addCase(resetOrganizationsTable, (state, action) => {
        return {
          ...OrganizationsInitialState,
          data: state.data,
        };
      })

      //  Refresh
      .addCase(fetchRefreshOrganizations.pending, (state, action) => {
        state.autoRefreshOrganizationState.loading = true;
      })
      .addCase(fetchRefreshOrganizations.fulfilled, (state, action) => {
        state.data = action.payload;
        state.autoRefreshOrganizationState.loading = false;
      })
      .addCase(fetchRefreshOrganizations.rejected, (state, action) => {
        state.autoRefreshOrganizationState.error = action.error.message;
        state.autoRefreshOrganizationState.loading = false;
      })

      //Auto Refresh
      .addCase(setOrganizationsAutoRefresh, (state, action) => {
        state.autoRefreshOrganizationState.currentIntervalId = action.payload.intervalId;
      })
      .addCase(switchOrganizationsAutoFresh, (state, action) => {
        state.autoRefreshOrganizationState.autoRefresh = action.payload.autoRefresh;
      })
      .addCase(stopOrganizationsAutoRefresh, (state, action) => {
        clearInterval(state.autoRefreshOrganizationState.currentIntervalId);
      })

      .addCase(createOrganizations.pending, (state, action) => {
        state.createOrganizationLoading = true;
      })
      .addCase(createOrganizations.fulfilled, (state, action) => {
        state.createOrganizationLoading = false;
      })
      .addCase(createOrganizations.rejected, (state, action) => {
        state.createOrganizationLoading = false;
        state.error = action.error.message;
      })
      .addCase(deleteOrganizations.pending, (state, action) => {
        state.deleteOrganizationLoading = true;
      })
      .addCase(deleteOrganizations.fulfilled, (state, action) => {
        state.deleteOrganizationLoading = false;
      })
      .addCase(deleteOrganizations.rejected, (state, action) => {
        state.deleteOrganizationLoading = false;
        state.error = action.error.message;
      })

      .addCase(setCurrentRecord, (state, action) => {
        state.currentRecord = action.payload;
      })

      .addCase(switchOnOrganization.pending, (state, action) => {
        state.switchLoading = true;
      })
      .addCase(switchOnOrganization.fulfilled, (state, action) => {
        state.switchLoading = false;
      })
      .addCase(switchOnOrganization.rejected, (state, action) => {
        state.switchLoading = false;
        state.error = action.error.message;
      })
      .addCase(switchOffOrganization.pending, (state, action) => {
        state.switchLoading = true;
      })
      .addCase(switchOffOrganization.fulfilled, (state, action) => {
        state.switchLoading = false;
      })
      .addCase(switchOffOrganization.rejected, (state, action) => {
        state.switchLoading = false;
        state.error = action.error.message;
      })

      //Modal
      .addCase(openCreateOrganizationsModal, (state, action) => {
        state.createOrganizationsModalState = true;
      })
      .addCase(closeCreateOrganizationsModal, (state, action) => {
        state.createOrganizationsModalState = false;
      })
      .addCase(openDeleteOrganizationsModal, (state, action) => {
        state.deleteOrganizationsModalState = true;
      })
      .addCase(closeDeleteOrganizationsModal, (state, action) => {
        state.deleteOrganizationsModalState = false;
      })
      .addCase(openTemplateModal, (state, action) => {
        state.templateModalState = true;
      })
      .addCase(closeTemplateModal, (state, action) => {
        state.templateModalState = false;
      });
  },
});

export default OrganizationsPageSlice.reducer;
